import { filter, map, mergeMap } from 'rxjs/operators';

import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { select, Store } from '@ngxs/store';

import { AsyncPipe, I18nPluralPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AlertsCenterCounterComponent } from '@app/client/alerts/components/counter/alerts-center-counter.component';
import { AlertsCenterService } from '@app/client/alerts/services/alerts-center.service';
import { AlertsState } from '@app/client/alerts/state/alerts.state';
import { languages, params } from '@app/configurations';
import { CommonAPI } from '@app/core/api/common.service';
import { MenuService } from '@app/core/api/menu.service';
import { AccountLimitDialogComponent } from '@app/core/components/account-limit-dialog/account-limit-dialog.component';
import { BaseCurrencies } from '@app/core/models/customer.models';
import { UserSetting } from '@app/core/models/user.models';
import { GlobalSideNavService } from '@app/core/services/global-side-nav.service';
import { UtilitiesService } from '@app/core/services/utilities.service';
import { SetUserCurrency } from '@app/core/store/states/currency/currency-state.actions';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { RouterPath } from '@app/core/store/states/router-nav/router-nav-state.model';
import { RouterNavState } from '@app/core/store/states/router-nav/router-nav.state';
import { SetUserSettings } from '@app/core/store/states/user/user-state.actions';
import { UserState } from '@app/core/store/states/user/user.state';

import { SignupService } from '@app/core/api/signup.service';
import { AppHasAuth, IsBilledApp, IsOauthApp, IsPortalApp, IsSignupApp } from '@app/core/utilities/app.utilities';
import { getUserInitials, logout } from '@app/core/utilities/user.utilities';
import { TourModule } from '@app/shared/modules/tour/tour.module';
import { environment } from '@env/environment';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { CustomerSelectorComponent } from '../customer-selector/customer-selector.component';
import { IconComponent } from '../icon/icon.component';
import { UserAvatarComponent } from '../user-avatar/user-avatar.component';

@Component({
  selector: 'adspt-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    NgClass,
    AsyncPipe,
    TourModule,
    NzDropDownModule,
    NzSwitchModule,
    NzMenuModule,
    NzRadioModule,
    NgTemplateOutlet,
    FormsModule,
    TranslatePipe,
    NgTemplateOutlet,
    I18nPluralPipe,
    IconComponent,
    UserAvatarComponent,
    CustomerSelectorComponent,
    AlertsCenterCounterComponent,
    RouterModule,
  ],
})
export class HeaderComponent implements OnInit {
  readonly unreadPluralMap = {
    '=0': 'alert-center.count.none',
    '=1': 'alert-center.count.singular',
    other: 'alert-center.count.plural',
  };
  readonly resources = {
    [languages.ENGLISH]: [
      {
        title: 'help',
        href: 'https://support.adspert.net/en/',
      },
      {
        title: 'webinar',
        href: 'https://www.adspert.net/webinars/',
      },
      {
        title: 'tutorials',
        href: 'https://support.adspert.net/en/collections/2723188-getting-started-with-adspert#tutorial-videos',
      },
    ],
    [languages.GERMAN]: [
      {
        title: 'help',
        href: 'https://support.adspert.net/de/',
      },
      {
        title: 'webinar',
        href: 'https://www.adspert.net/de/webinare/',
      },
      {
        title: 'tutorials',
        href: 'https://support.adspert.net/de/collections/2749177-tutorial-videos',
      },
    ],
    [languages.CHINESE]: [
      {
        title: 'help',
        href: 'https://support.adspert.net/zh-CN/',
      },
      {
        title: 'tutorials',
        href: 'https://space.bilibili.com/3493144580590458',
      },
    ],
  };
  readonly appHasAuth = AppHasAuth();
  readonly isOauthApp = IsOauthApp();
  readonly isBilledApp = IsBilledApp();
  readonly isSignupApp = IsSignupApp();
  readonly isPortalApp = IsPortalApp();

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private translate = inject(TranslateService);
  private utilitiesService = inject(UtilitiesService);
  private sideBarService = inject(GlobalSideNavService);
  private sa = inject(SignupService);
  private store = inject(Store);
  private ms = inject(NzModalService);
  private commonAPI = inject(CommonAPI);
  readonly ac = inject(AlertsCenterService);
  readonly menuService = inject(MenuService);

  readonly currencies = BaseCurrencies;
  readonly languages = Object.values(languages);
  readonly switchLoader = signal(false);
  readonly currencyLoader = signal(false);

  currentLang = this.translate.currentLang.toUpperCase();

  readonly user = select(UserState.user);
  readonly customersCount = select(CustomerState.customersCount);
  readonly userCurrency = select(CustomerState.userCurrency);
  readonly customerId = select(CustomerState.customerId);
  readonly customer = select(CustomerState.customer);
  readonly isBillingSubsidiary = select(CustomerState.isBillingSubsidiary);
  readonly isCustomerFull = select(CustomerState.isCustomerFull);
  readonly isTrial = select(CustomerState.isTrial);
  readonly isEssentialSegment = select(CustomerState.isEssentialSegment);
  readonly isAgencyAdmin = select(CustomerState.isAgencyAdmin);
  readonly isAgencyClient = select(CustomerState.isAgencyClient);
  readonly isSysAdmin = select(UserState.isSysAdmin);
  readonly unreadCount = select(AlertsState.unreadCount);
  readonly canSeeNotifications = select(CustomerState.hasNotificationsAccess);
  readonly platformsAccessible = select(CustomerState.platformsAccessible);
  readonly customerCanceled = select(CustomerState.canceled);
  readonly queryParams = select(RouterNavState.queryParams);
  readonly adGroupCampaignTypeSetting = select(UserState.setting(UserSetting.AdGroupCampaignType, { visible: true }));

  readonly hideLinks = input(true);
  readonly disableLinks = input(true);

  readonly intitals = computed(() => getUserInitials(this.user()));
  readonly isSysAdminOnly = computed(() => !!this.user()?.sysAdmin);
  readonly signupUrl = computed(() => `/signup/connect-account?${params.CUSTOMER_ID}=${this.customerId() || 0}`);
  readonly apolloUrl = computed(() => `/monitor?${params.CUSTOMER_ID}=${this.customerId() || 0}`);
  readonly agencyAdminUrl = computed(
    () => `${RouterPath.AgencySettings}?${params.CUSTOMER_ID}=${this.customer()?.mainCustomerId || this.customerId()}`,
  );
  readonly subscriptionUrl = computed(
    () => `${RouterPath.CustomerSubscriptionSettings}?${params.CUSTOMER_ID}=${this.customerId()}`,
  );
  readonly showAgencySettingsLink = computed(
    () => this.isAgencyAdmin() || (this.isSysAdmin() && this.isAgencyClient()),
  );

  setCurrency(currency, canContinue) {
    if (canContinue) return;
    this.currencyLoader.set(true);
    this.store.dispatch(new SetUserCurrency(currency, true)).subscribe(() => {
      if (environment.production) {
        location.reload();
      }
    });
  }

  toggleAdGroupCampaignTypeSetting(newValue): void {
    if (this.switchLoader()) return; // dont trigger if already loading
    this.switchLoader.set(true);
    this.store
      .dispatch(new SetUserSettings({ [UserSetting.AdGroupCampaignType]: { visible: newValue } }, false))
      .subscribe(() => this.switchLoader.set(false));
  }

  openSideBar(event: any) {
    event.preventDefault();

    this.sideBarService.toggle();
  }
  onLanguageChange(data: string) {
    this.currentLang = data;
    if (this.isPortalApp) {
      window.history.replaceState(null, null, `?lang=${this.currentLang.toLocaleLowerCase()}`);
    }
    {
      if (!this.appHasAuth) {
        this.translate.use(data.toLowerCase());
        if (!this.sa.isCustomerSignup) {
          this.sa.updateUserInfo({ lang: data }).subscribe();
        }
      } else {
        this.commonAPI.updateUserInfo(this.user().userId, { lang: data }).subscribe(() => location.reload());
      }
    }
  }
  logout(event) {
    event.preventDefault();
    logout();
  }

  onNotificationBellClick(): void {
    this.ac.toggleDrawer();
  }

  onAddAccountLimitClick(): void {
    const isAgency = this.isAgencyAdmin() || (this.isSysAdmin() && this.isAgencyClient());
    const translations = this.translate.instant('common.account-limit');

    this.ms.create({
      nzClosable: true,
      nzTitle:
        this.isTrial() && this.isEssentialSegment()
          ? translations['dialog-trial-title']
          : isAgency
            ? translations['dialog-account-group-title']
            : translations['dialog-subscription-title'],
      nzContent: AccountLimitDialogComponent,
      nzFooter: null,
      nzClassName: 'a-modal-medium',
    });
  }

  private _setDocumentTitle() {
    try {
      let tmp = Object.assign(this.route, {});
      while (tmp && tmp.firstChild) {
        tmp = tmp.firstChild;
      }
      this.utilitiesService.setTitle(tmp.snapshot.data.title);
    } catch (err) {}
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.route),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data),
      )
      .subscribe((event) => {
        this.utilitiesService.setTitle(event.title);
      });
  }

  constructor() {
    this.translate.onLangChange.subscribe(() => (this.currentLang = this.translate.currentLang.toUpperCase()));
  }

  ngOnInit() {
    this._setDocumentTitle();
  }
}
